/**
 * The extensions this console build ships with.
 *
 * Bundles are pinned at build time rather than resolved from the marketplace
 * registry at runtime, so a console deploy is what picks up an extension update.
 * Each pin is a manifest (inlined by Vite, cheap, always present) plus a loader
 * for the bundle (lazy, large).
 *
 * To add another extension: add a row to `PINNED_EXTENSIONS` in
 * `scripts/pinnedExtensions.mjs`, add its `link:` dependency in package.json,
 * and add its two `define` identifiers to `pinnedExtensionBundles.ts`. Nothing
 * here, in the registry, or in the document-type catalog changes.
 */

import { registerBuiltinCanvasExtension } from '../canvas/builtinCanvasExtension';
import { registerBrowserExtension } from '@nimbalyst/web-editors/registry';
import { registerBuiltinHtmlPageExtension } from './builtinHtmlPageExtension';
import { registerBuiltinMonacoExtension } from './builtinMonacoExtension';
import {
  PINNED_EXTENSION_MANIFESTS,
  PINNED_EXTENSION_SLUGS,
  loadPinnedExtensionBundle,
} from '@nimbalyst/web-editors/bundles';

/** The pinned spreadsheet's own `manifest.json`, inlined at build time. */
export const PINNED_CSV_EXTENSION_MANIFEST = PINNED_EXTENSION_MANIFESTS['csv-spreadsheet'];

let registered = false;

/**
 * Declare every pinned extension. Idempotent, and safe to call from a render
 * path: it registers manifests only and imports no bundle.
 */
export function registerPinnedBrowserExtensions(): void {
  if (registered) return;
  registered = true;
  // Not a pin -- Project Canvas ships in the collab bundle, not as an extension
  // package -- but it declares and loads through the same two stages, so this
  // is where it belongs rather than in a second registration path the catalog
  // would also have to know about. See `builtinCanvasExtension.ts`.
  registerBuiltinCanvasExtension();
  // The `html` page type: the sandboxed HTML embed plus Monaco for its source.
  // Before Monaco, whose code suffixes include `.html`, so a lookup by suffix
  // prefers the page type. See `builtinHtmlPageExtension.ts`.
  registerBuiltinHtmlPageExtension();
  // Also not a pin: Monaco ships in `@nimbalyst/runtime`. Same two stages, and
  // the lazy half matters more here than anywhere else -- it is the largest
  // bundle this console can load. See `builtinMonacoExtension.ts`.
  registerBuiltinMonacoExtension();
  for (const slug of PINNED_EXTENSION_SLUGS) {
    registerBrowserExtension({
      manifest: PINNED_EXTENSION_MANIFESTS[slug],
      load: () => loadPinnedExtensionBundle(slug),
    });
  }
}

/** Undo the once-only guard so a test can register into a fresh registry. */
export function resetPinnedBrowserExtensionsForTests(): void {
  registered = false;
}
